import React, { useEffect, useState } from "react";
import axios from "axios";
import API_URL from "../config";

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [isAuthChecking, setIsAuthChecking] = useState(true);

  const token = localStorage.getItem("token");

  // ── AUTH GUARD ── redirect anyone without a token
  useEffect(() => {
    if (!token) {
      window.location.href = "/login";
    }
    else {
      setIsAuthChecking(false); // Token found, stop loading
    }
  }, [token]);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/orders`, {
        headers: { Authorization: token }
      });
      setOrders(res.data);
    } catch (err) {
      console.log(err);
      // If 401/403, token is invalid — boot them out
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        localStorage.removeItem("token");
        window.location.href = "/login";
      }
    }
  };

  useEffect(() => {
    if (!isAuthChecking && token) fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthChecking, token]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(
        `${API_URL}/api/orders/${id}`,
        { status },
        { headers: { Authorization: token } }
      );
      fetchOrders();
    } catch {
      alert("Status update failed");
    }
  };
  
// DO NOT RENDER ANYTHING UNTIL AUTH IS VERIFIED
  if (isAuthChecking || !token) {
     return <div style={{ padding: "50px", textAlign: "center" }}>Authenticating...</div>;
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        .ao-root { font-family:'DM Sans',sans-serif; background:#fdf9f6; min-height:100vh; padding:32px 28px 64px; max-width:900px; margin:0 auto; }
        @media(max-width:600px){.ao-root{padding:20px 14px 48px}}

        .ao-title {
          font-family:'Cormorant Garamond',serif;
          font-size:clamp(22px,4vw,32px);
          font-weight:300;
          color:#1a1410;
          margin:0 0 24px;
          letter-spacing:-.01em;
        }

        .ao-empty {
          text-align:center;
          padding:48px 20px;
          color:#a09890;
          font-size:14px;
          font-weight:300;
        }

        .ao-card {
          background:#fff;
          border:1px solid #ede8e2;
          padding:18px 20px;
          margin-bottom:14px;
        }
        @media(max-width:600px){.ao-card{padding:14px 14px}}

        .ao-card-header {
          display:flex;
          justify-content:space-between;
          align-items:flex-start;
          flex-wrap:wrap;
          gap:8px;
          margin-bottom:12px;
        }
        .ao-customer-name {
          font-family:'Cormorant Garamond',serif;
          font-size:18px;
          font-weight:400;
          color:#1a1410;
          display:block;
        }
        .ao-customer-meta {
          font-size:12px;
          font-weight:300;
          color:#7a6e68;
          display:block;
          margin-top:2px;
          line-height:1.5;
        }
        .ao-status-badge {
          font-size:9px;
          font-weight:500;
          letter-spacing:.14em;
          text-transform:uppercase;
          padding:3px 10px;
          border-radius:20px;
          white-space:nowrap;
        }
        .ao-status-pending    { background:#fef3c7; color:#92400e; }
        .ao-status-processing { background:#fde68a; color:#78350f; }
        .ao-status-shipped    { background:#dbeafe; color:#1e40af; }
        .ao-status-delivered  { background:#d1fae5; color:#065f46; }
        .ao-status-default    { background:#f3f4f6; color:#374151; }

        .ao-divider { border:none; border-top:1px solid #f0ebe5; margin:10px 0; }

        .ao-items { display:flex; flex-direction:column; gap:4px; margin-bottom:8px; }
        .ao-item {
          display:flex;
          justify-content:space-between;
          font-size:12px;
          font-weight:300;
          color:#5a524c;
        }
        .ao-item span:last-child { font-weight:500; color:#1a1410; }

        .ao-total {
          font-size:13px;
          font-weight:500;
          color:#1a1410;
          text-align:right;
          margin-top:4px;
        }

        .ao-actions { display:flex; gap:6px; margin-top:14px; flex-wrap:wrap; }
        .ao-btn {
          font-family:'DM Sans',sans-serif;
          font-size:9.5px;
          font-weight:500;
          letter-spacing:.13em;
          text-transform:uppercase;
          padding:6px 14px;
          border:none;
          cursor:pointer;
          transition:opacity .18s, transform .18s;
          border-radius:2px;
        }
        .ao-btn:hover { opacity:.85; transform:translateY(-1px); }
        .ao-btn-pending    { background:#e5e7eb; color:#374151; }
        .ao-btn-processing { background:#fbbf24; color:#fff; }
        .ao-btn-shipped    { background:#3b82f6; color:#fff; }
        .ao-btn-delivered  { background:#10b981; color:#fff; }
      `}</style>

      <div className="ao-root">
        <h1 className="ao-title">Orders Dashboard</h1>

        {orders.length === 0 && (
          <div className="ao-empty">No orders yet.</div>
        )}

        {orders.map(order => {
          const statusClass =
            order.status === "pending"    ? "ao-status-pending"    :
            order.status === "processing" ? "ao-status-processing" :
            order.status === "shipped"    ? "ao-status-shipped"    :
            order.status === "delivered"  ? "ao-status-delivered"  :
            "ao-status-default";

          return (
            <div key={order._id} className="ao-card">

              <div className="ao-card-header">
                <div>
                  <span className="ao-customer-name">{order.customerName}</span>
                  <span className="ao-customer-meta">
                    {order.phone}<br/>{order.address}
                  </span>
                </div>
                <span className={`ao-status-badge ${statusClass}`}>
                  {order.status}
                </span>
              </div>

              <hr className="ao-divider" />

              <div className="ao-items">
                {order.items.map((item, index) => (
                  <div key={index} className="ao-item">
                    <span>{item.name} × {item.quantity}</span>
                    <span>{item.price * item.quantity} PKR</span>
                  </div>
                ))}
              </div>

              <p className="ao-total">Total: {order.total} PKR</p>

              <div className="ao-actions">
                <button onClick={() => updateStatus(order._id, "pending")}    className="ao-btn ao-btn-pending">Pending</button>
                <button onClick={() => updateStatus(order._id, "processing")} className="ao-btn ao-btn-processing">Processing</button>
                <button onClick={() => updateStatus(order._id, "shipped")}    className="ao-btn ao-btn-shipped">Shipped</button>
                <button onClick={() => updateStatus(order._id, "delivered")}  className="ao-btn ao-btn-delivered">Delivered</button>
              </div>

            </div>
          );
        })}
      </div>
    </>
  );
}